import { Link } from "react-router-dom";
import { CalendarDays, MapPin, Layers, Radio } from "lucide-react";
import { useLang, useT, fmtDay, fmtDate } from "../lib/i18n";
import { TipGrid } from "../components/site/TipCard";
import { Crumbs, Faq, Section, VipCta } from "../components/site/Blocks";
import { Wrap } from "./PublicPage";

const SUBL = { de: { overview: "Übersicht", tips: "Wetttipps", prize: "Preisgeld", schedule: "Spielplan", draw: "Draw", winners: "Sieger" },
  en: { overview: "Overview", tips: "Betting Tips", prize: "Prize Money", schedule: "Schedule", draw: "Draw", winners: "Winners" } };

const Table = ({ head, rows, testid }) => (
  <div className="surface overflow-x-auto" data-testid={testid}>
    <table className="w-full text-sm">
      <thead className="text-left text-xs uppercase tracking-wider text-muted-foreground"><tr>{head.map((h) => <th key={h} className="px-4 py-3">{h}</th>)}</tr></thead>
      <tbody>{rows.map((r, i) => <tr key={i} className="border-t border-border">{r.map((c, j) => <td key={j} className="px-4 py-3">{c}</td>)}</tr>)}</tbody>
    </table>
  </div>
);

function Events({ list, odds, empty }) {
  const lang = useLang();
  if (!list?.length) return <p className="surface p-6 text-sm text-muted-foreground">{empty}</p>;
  return (
    <div className="surface divide-y divide-border" data-testid="schedule-list">
      {list.map((e) => (
        <div key={e.id} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 text-sm">
          <span className="w-28 shrink-0 text-xs text-muted-foreground">{fmtDate(new Date(e.time * 1000).toISOString(), lang)}{e.round ? ` · R${e.round}` : ""}</span>
          <span className="flex-1 font-medium">{e.home} <span className="text-muted-foreground">vs</span> {e.away}</span>
          {e.ss && <span className="font-num font-bold">{e.ss}</span>}
          {odds && e.odds && <span className="font-num text-[var(--ball)]">{e.odds.home_od} / {e.odds.away_od}</span>}
        </div>
      ))}
    </div>
  );
}

export default function TournamentPage({ p }) {
  const lang = useLang();
  const t = useT();
  const d = p.data;
  const tr = d.tournament;
  const sc = d.schedule;
  const noData = t("Noch keine Daten verfügbar – sobald das Turnier läuft, erscheinen hier Spiele, Quoten und Ergebnisse.", "No data yet – matches, odds and results appear here once the tournament starts.");
  return (
    <>
      <section className="relative overflow-hidden border-b border-border">
        <div className="court-lines absolute inset-0" />
        <Wrap className="relative pb-8">
          <Crumbs items={p.crumbs} />
          <span className="rounded-full border border-[var(--ball)]/40 px-3 py-1 text-xs font-semibold text-[var(--ball)]">{tr.category}</span>
          <h1 className="mt-4 font-display text-5xl font-extrabold leading-none sm:text-6xl" data-testid="page-h1">{p.meta.title}</h1>
          <div className="mt-5 flex flex-wrap gap-5 text-sm text-muted-foreground" data-testid="tournament-facts">
            <span className="flex items-center gap-1.5"><MapPin size={15} />{tr.city}, {tr.country}</span>
            <span className="flex items-center gap-1.5"><Layers size={15} />{tr.surface}</span>
            <span className="flex items-center gap-1.5"><CalendarDays size={15} />{fmtDay(tr.start_date, lang)} – {fmtDay(tr.end_date, lang)}</span>
            {tr.prize_money_total && <span className="font-num text-foreground">{tr.prize_money_total}</span>}
          </div>
          <nav className="mt-8 flex gap-2 overflow-x-auto pb-1" data-testid="tournament-subnav">
            {Object.entries(d.subs).map(([k, url]) => (
              <Link key={k} to={url} data-testid={`subnav-${k}`} className={`whitespace-nowrap rounded-full border px-4 py-2 text-sm transition-colors ${d.sub === k ? "border-primary bg-primary text-primary-foreground" : "border-border hover:border-primary/50"}`}>{SUBL[lang][k]}</Link>
            ))}
          </nav>
        </Wrap>
      </section>
      <Wrap>
        {d.sub === "overview" && d.seo?.intro_html && <div className="prose-tt max-w-3xl" data-testid="tournament-intro" dangerouslySetInnerHTML={{ __html: d.seo.intro_html }} />}
        {d.sub !== "overview" && d.seo?.[`${d.sub}_html`] && <div className="prose-tt max-w-3xl" data-testid="tournament-sub-text" dangerouslySetInnerHTML={{ __html: d.seo[`${d.sub}_html`] }} />}
        {["overview", "tips"].includes(d.sub) && <Section title={t(`${tr.name} Wetttipps`, `${tr.name} betting tips`)} testid="tournament-tips"><TipGrid tips={d.tips} /></Section>}
        {sc?.live?.length > 0 && <Section title={<span className="flex items-center gap-2"><Radio className="live-dot text-[var(--win)]" />Live</span>}><Events list={sc.live} /></Section>}
        {["overview", "schedule", "tips"].includes(d.sub) && (
          <Section title={t("Spielplan & Quoten", "Schedule & odds")} testid="tournament-schedule"><Events list={(sc?.upcoming || []).slice(0, d.sub === "schedule" ? 40 : 8)} odds empty={noData} /></Section>
        )}
        {["schedule", "draw"].includes(d.sub) && (
          <Section title={t("Ergebnisse", "Results")} testid="tournament-results"><Events list={sc?.results} empty={noData} /></Section>
        )}
        {d.sub === "draw" && tr.draw_html && <div className="prose-tt mt-10" dangerouslySetInnerHTML={{ __html: tr.draw_html }} />}
        {d.sub === "prize" && (
          <Section title={t("Preisgeld nach Runde", "Prize money by round")}>
            {tr.prize_table?.length ? <><Table testid="prize-table" head={[t("Runde", "Round"), t("Punkte", "Points"), t("Preisgeld", "Prize money")]} rows={tr.prize_table.map((r) => [r.round, r.points, r.amount])} />
              <p className="mt-3 text-xs text-muted-foreground" data-testid="prize-note">{t("Einzel. Beträge basieren auf der Vorjahresausgabe bzw. der Kategorie und werden nach offizieller Bekanntgabe aktualisiert.", "Singles. Amounts are based on the previous edition or category and are updated once officially announced.")}</p></>
              : <p className="surface p-6 text-sm text-muted-foreground">{t("Preisgeld wird nach offizieller Bekanntgabe veröffentlicht.", "Prize money will be published once officially announced.")}</p>}
          </Section>
        )}
        {d.sub === "winners" && (
          <Section title={t("Sieger", "Past winners")}>
            {tr.past_winners?.length ? <Table testid="winners-table" head={[t("Jahr", "Year"), t("Konkurrenz", "Draw"), t("Sieger", "Winner"), t("Finalist", "Runner-up")]} rows={tr.past_winners.map((w) => [w.year, w.draw || (tr.tour === "wta" ? t("Damen", "Women") : t("Herren", "Men")), w.winner, w.runner_up])} />
              : <p className="surface p-6 text-sm text-muted-foreground">{t("Siegerliste folgt.", "Winners list coming soon.")}</p>}
          </Section>
        )}
        <VipCta />
        <Faq items={p.faq} />
      </Wrap>
    </>
  );
}
